<template>
    <template v-if="isMetaPreview">
        <div
            ref="container"
            class="campaign-banner"
            :class="{ 'campaign-banner--dark': isDarkTheme, 'campaign-banner--meta': isMetaPreview }"
            :style="{
                width: `${previewAdInputSize.width}px`
            }"
        >
            <div class="ad-preview-header-wrapper">
                <Select v-model="adPreviewType" class="header-action-select" transfer>
                    <Option v-for="option in previewOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                        <span v-if="option.subLabel">{{ option.subLabel }}</span>
                    </Option>
                </Select>
            </div>

            <div class="campaign-banner__content">
                <MetaPreviewFrame
                    :meta-overwrites="metaOverwrites"
                    :master-template="masterTemplate"
                    :ad-preview-type="adPreviewType"
                    @setInStreamValue="setInStreamValue"
                >
                    <LiveBannerPreview
                        :id="bannerId"
                        :scaling-factor="previewAdInputSize.adScalingFactor"
                        :preview-values="previewValues"
                        :master-template="masterTemplate"
                        :playback-namespace="playbackNamespace"
                        :meta-preview-size="previewAdInputSize"
                        @bannerLoaded="onBannerLoaded"
                    />
                </MetaPreviewFrame>
            </div>

            <div class="campaign-banner__footer">
                <BannerFooterName :is-dark-theme="false" :banner-name="bannerName" />
                <BannerFooterControls :banner-id="bannerId" :playback-namespace="playbackNamespace" />

                <EditorBannerFooterOptions
                    :is-meta-preview="isMetaPreview"
                    :master-template="masterTemplate"
                    :options-visible="optionsVisible"
                    @updateOptionsVisible="optionsVisible = $event"
                    @updateStarted="onUpdateStarted"
                    @update="onUpdate"
                    @familyUpdated="onFamilyUpdated"
                    @remove="onRemove"
                />
            </div>
        </div>
    </template>

    <template v-else>
        <CampaignBanner
            :banner-name="bannerName"
            :master-template-height="displayDimensions.height"
            :master-template-width="displayDimensions.width"
            :max-height="maxHeight"
            :max-width="maxWidth"
            :scaling-factor="scalingFactor"
            :resize-mode="resizeMode"
            @toggleResizeMode="onToggleResizeMode"
            @updateInternalScalingFactor="internalScalingFactor = $event"
        >
            <template #bannerContainer>
                <LiveBannerPreview
                    :id="bannerId"
                    :scaling-factor="internalScalingFactor"
                    :preview-values="previewValues"
                    :master-template="masterTemplate"
                    :playback-namespace="playbackNamespace"
                    @bannerLoaded="onBannerLoaded"
                />
            </template>

            <template #bannerFooter>
                <BannerFooterName :is-dark-theme="false" :banner-name="bannerName" />
                <BannerFooterControls :banner-id="bannerId" :playback-namespace="playbackNamespace" />

                <EditorBannerFooterOptions
                    :is-meta-preview="isMetaPreview"
                    :resize-mode="resizeMode"
                    :master-template="masterTemplate"
                    :options-visible="optionsVisible"
                    @updateOptionsVisible="optionsVisible = $event"
                    @toggleResizeMode="onToggleResizeMode"
                    @updateStarted="onUpdateStarted"
                    @update="onUpdate"
                    @familyUpdated="onFamilyUpdated"
                    @newSize="onNewSize"
                    @remove="onRemove"
                />
            </template>
        </CampaignBanner>
    </template>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { Editable, MasterTemplate, AEMasterTemplate, InDesignMasterTemplate } from "hoxton-types";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import setLayerSettings from "hoxton-shared/utils/setLayerSettings";

import { CampaignGetters } from "@/store/modules/campaign";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { formatTemplateSizeLabel, getTemplateDisplayDimensions } from "@/utils";

import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import LiveBannerPreview from "@/components/Campaign/LiveBannerPreview.vue";
import MetaPreviewFrame, { META_AD_PREVIEW_OPTIONS } from "@/components/Campaign/AdPreview/MetaPreviewFrame.vue";
import BannerFooterName from "@/components/Previews/BannerFooter/BannerFooterName.vue";
import BannerFooterControls from "@/components/Previews/BannerFooter/BannerFooterControls.vue";
import EditorBannerFooterOptions from "@/components/Previews/BannerFooter/EditorBannerFooterOptions.vue";

import { BannerEvents } from "@/enums/banners";
import { getBannerIdFromMasterTemplate } from "@/utils/getBannerIframeId";
import type { MetaOverwrites, ComputedOverwrite } from "@/types/types";

export default defineComponent({
    name: "DeliverablePreview",
    components: {
        CampaignBanner,
        LiveBannerPreview,
        MetaPreviewFrame,
        EditorBannerFooterOptions,
        BannerFooterName,
        BannerFooterControls
    },

    props: {
        masterTemplate: {
            type: Object as PropType<MasterTemplate>,
            required: true
        },

        maxWidth: {
            type: Number,
            required: true
        },

        maxHeight: {
            type: Number,
            required: true
        },

        scalingFactor: {
            type: Number,
            default: 0
        },

        isMetaPreview: {
            type: Boolean,
            default: false
        },

        metaOverwrites: {
            type: Object as PropType<MetaOverwrites>,
            default: () => ({})
        },

        playbackNamespace: {
            type: String,
            required: true
        }
    },

    emits: [BannerEvents.BannerLoaded, "familyUpdated", "update", "updateStarted", "remove", "newSize"],

    setup(_, { emit }) {
        const { getEditablePreviewValue, getEditableSettings } = useEditableMethods({ emit });

        return {
            getEditablePreviewValue,
            getEditableSettings
        };
    },

    data() {
        return {
            resizeMode: false,
            optionsVisible: false,
            previewOptions: META_AD_PREVIEW_OPTIONS,
            adPreviewType: "instagram-feed" as keyof typeof META_AD_PREVIEW_OPTIONS,
            internalScalingFactor: 1,
            inStreamVideoIsPlaying: false
        };
    },

    computed: {
        bannerId() {
            return getBannerIdFromMasterTemplate(this.masterTemplate, this.playbackNamespace);
        },

        isDarkTheme() {
            return this.$store.state.ui.themeMode.theme === "dark";
        },

        previewAdInputSize() {
            if (!this.isMetaPreview) {
                return {};
            }

            const inStreamWidth = META_AD_PREVIEW_OPTIONS[this.adPreviewType]?.inStreamWidth;
            const width = META_AD_PREVIEW_OPTIONS[this.adPreviewType].width;

            let height = META_AD_PREVIEW_OPTIONS[this.adPreviewType].height;
            let maxHeight = META_AD_PREVIEW_OPTIONS[this.adPreviewType].maxHeight;
            let minHeight = META_AD_PREVIEW_OPTIONS[this.adPreviewType].minHeight;

            let adScalingFactor = 1;

            if (inStreamWidth && !this.inStreamVideoIsPlaying && this.masterTemplate.width > inStreamWidth) {
                adScalingFactor = inStreamWidth / this.masterTemplate.width;
            } else if (this.masterTemplate.width > width) {
                adScalingFactor = width / this.masterTemplate.width;
            }

            switch (this.adPreviewType) {
                case "instagram-feed":
                case "news-feed":
                case "news-feed-desktop":
                case "news-feed-video":
                case "news-feed-tab":
                case "facebook-in-stream-video-mobile":
                case "facebook-in-stream-video-desktop":
                case "facebook-in-stream-banner-mobile":
                case "facebook-in-stream-banner-desktop":
                case "facebook-in-stream-full-video":
                case "facebook-in-stream-full-banner":
                    if (this.masterTemplate.width < width) {
                        height = this.masterTemplate.height;
                    }

                    break;

                default:
                    break;
            }

            return {
                adScalingFactor,
                width,
                height,
                maxHeight,
                minHeight
            };
        },

        campaignId() {
            return this.$route.params.campaignId;
        },

        isPsd() {
            return this.masterTemplate.adType === TemplateType.PSD;
        },

        computedOverwrites(): ComputedOverwrite[] {
            const editables = this.$store.getters[CampaignGetters.templateEditables](
                this.masterTemplate._id
            ) as Editable[];

            return editables.map(editable => {
                // can use editable groups this way since the results are comming from the store curated
                const groupsAffectingEditable = editable.defaultValues[0]?.editableGroups || [];
                const value = this.getEditablePreviewValue(editable, groupsAffectingEditable, this.masterTemplate);
                const settings = this.getEditableSettings(editable, groupsAffectingEditable, this.masterTemplate._id);
                const templateLayer = editable.layers?.find(
                    ({ masterTemplateId }) => masterTemplateId === this.masterTemplate._id
                );

                if (!this.isPsd || !editable.layers || !templateLayer?.value) {
                    return {
                        value,
                        name: editable.name,
                        type: editable.type,
                        isPublisherPlatform: !!editable.publisherPlatform,
                        layer: null,
                        settings,
                        htmlTag: "manifest"
                    };
                }

                const layer = setLayerSettings(templateLayer.value, settings);

                return {
                    value,
                    name: editable.name,
                    type: editable.type,
                    isPublisherPlatform: !!editable.publisherPlatform,
                    layer,
                    settings,
                    htmlTag: "manifest"
                };
            });
        },

        previewValues() {
            return JSON.stringify({
                computedOverwrites: this.computedOverwrites,
                masterTemplate: {
                    _id: this.masterTemplate._id,
                    persistentKey: this.masterTemplate.persistentKey,
                    width: this.masterTemplate.width,
                    height: this.masterTemplate.height,
                    ...((this.masterTemplate as AEMasterTemplate)?.aeInfo && {
                        aeInfo: {
                            completed: (this.masterTemplate as AEMasterTemplate)?.aeInfo?.completed,
                            plainlyProjectId: (this.masterTemplate as AEMasterTemplate)?.aeInfo?.plainlyProjectId,
                            plainlyTemplateId: (this.masterTemplate as AEMasterTemplate)?.aeInfo?.plainlyTemplateId
                        }
                    }),
                    ...((this.masterTemplate as InDesignMasterTemplate).sourceDocuments && {
                        sourceDocuments: (this.masterTemplate as InDesignMasterTemplate).sourceDocuments
                    }),
                    ...((this.masterTemplate as InDesignMasterTemplate).defaultSharedOverrides && {
                        defaultSharedOverrides: (this.masterTemplate as InDesignMasterTemplate).defaultSharedOverrides
                    }),
                    reportingLabel: this.masterTemplate.reportingLabel
                },
                previewUrl: this.masterTemplate.preview,
                campaignId: this.campaignId,
                metadata: [
                    {
                        fieldName: "language",
                        value: this.selectedLanguage
                    }
                ]
            });
        },

        selectedLanguage() {
            return this.$store.state.editor.selectedLanguage;
        },

        bannerName() {
            return `${formatTemplateSizeLabel(this.masterTemplate)} - ${this.masterTemplate.name}`;
        },

        displayDimensions() {
            return getTemplateDisplayDimensions(this.masterTemplate);
        }
    },

    methods: {
        onBannerLoaded() {
            this.$emit(BannerEvents.BannerLoaded);
        },

        onFamilyUpdated(masterTemplateIds: string[]) {
            this.$emit("familyUpdated", masterTemplateIds);
        },

        onNewSize() {
            this.$emit("newSize", this.masterTemplate);
            this.onOptionsHide();
        },

        onOptionsHide() {
            this.optionsVisible = false;
        },

        onRemove() {
            this.$emit("remove", this.masterTemplate._id);
        },

        onUpdate() {
            this.$emit("update", this.masterTemplate._id);
        },

        onUpdateStarted() {
            this.$emit("updateStarted", this.masterTemplate._id);
        },

        onToggleResizeMode(resizeMode: boolean) {
            this.resizeMode = resizeMode;
            this.onOptionsHide();
        },

        setInStreamValue(value: boolean) {
            this.inStreamVideoIsPlaying = value;
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.ad-preview-header-wrapper {
    padding: 10px;
    background-color: white;

    .header-action-select {
        max-width: unset;

        span {
            display: block;
            font-size: 12px;
        }
    }
}

.campaign-edit__deliverable {
    display: inline-block;
    margin: 0 15px 15px 0;
    vertical-align: top;

    &--resizable {
        display: block;
    }
}

/* we need to duplicate these campaign-banner styles for now, but we should unify them at some point */
$secondaryBackColor: #1b1f28;

.campaign-banner {
    overflow: hidden;

    border: 1px solid $cmp-light-border-color;

    display: flex;
    flex-direction: column;

    box-sizing: content-box;
}

.campaign-banner--dark {
    border-color: $darktheme40;
}

.campaign-banner--meta {
    border: none;

    .campaign-banner__content {
        flex: none;
    }
}

.campaign-banner__content {
    flex: 1 1 auto;

    display: flex;

    position: relative; // for the qa preview annotation layer
}

.campaign-banner__footer {
    color: $cmp-light-font-color;
}

.campaign-banner__footer__name {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.campaign-banner__footer__template-name {
    padding: 10px;
}

.campaign-banner__checkbox {
    width: 100%;
    height: 100%;

    position: relative;
    margin-right: 0;

    .ivu-checkbox {
        display: flex;

        position: absolute;
        top: 10px;
        left: 10px;
        font-size: 20px;

        &-inner {
            width: 20px;
            height: 20px;

            &:after {
                height: 14px;
                left: 8px;
            }
        }
    }

    .ivu-checkbox-label-text {
        margin: 0;
        margin-right: 0;
        padding: 0;
        display: flex;

        height: 100%;
    }
}

$height: 40px;
$button-width: 40px;

.campaign-banner__footer {
    width: 100%;
    height: $height;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: center;

    .campaign-banner__footer__name-container {
        .ivu-icon {
            cursor: pointer;
        }
    }

    .campaign-banner__footer__name-container {
        flex: 1 0;
        text-align: left;
        overflow: hidden;
    }

    .campaign-banner__footer__name {
        font-size: 12px;
        padding-left: 12px;
    }
}
</style>
